import {
  Activity,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Building2,
  Calendar,
  Camera,
  ChartNoAxesCombined,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleCheck,
  CircleX,
  Copy,
  CreditCard,
  Crop,
  Download,
  EllipsisVertical,
  Eye,
  File,
  FileBarChart,
  FileCode,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  Folder,
  FolderCog,
  FolderOpen,
  Funnel,
  GitMerge,
  Grid2x2,
  Hash,
  History,
  Home,
  Info,
  KeyRound,
  Landmark,
  Languages,
  Layers,
  Layers3,
  Link2,
  List,
  ListChecks,
  LoaderCircle,
  LogOut,
  MapPin,
  Moon,
  Network,
  Package,
  Pencil,
  Plus,
  Power,
  Printer,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Save,
  Search,
  SearchX,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Sun,
  Trash2,
  TriangleAlert,
  Undo2,
  Upload,
  User,
  Users,
  UsersRound,
  Vault,
  Wallet,
  X,
  ZoomIn,
  ZoomOut,
  type LucideProps,
} from 'lucide-react';
import { createElement, type ComponentType } from 'react';
import { cn } from '@/lib/utils';

/**
 * The single icon source for the application (rule I1). Product code imports
 * from here and never from `lucide-react`, so one action always carries one
 * glyph and a swap happens in a single file.
 *
 * Names are the canonical lucide names of the installed version (rule I2).
 * Several entries in the icon table are spelled with names that lucide has
 * since retired into aliases; the canonical spelling is used instead and the
 * glyph is unchanged:
 *
 * | Icon table | Canonical name in lucide-react 1.x |
 * | ---------- | ---------------------------------- |
 * | `Filter`        | `Funnel`           |
 * | `MoreVertical`  | `EllipsisVertical` |
 * | `Loader2`       | `LoaderCircle`     |
 * | `CheckCircle2`  | `CircleCheck`      |
 * | `XCircle`       | `CircleX`          |
 * | `AlertTriangle` | `TriangleAlert`    |
 */

export type IconComponent = ComponentType<LucideProps>;

/** Rule I5. Pixel sizes, for callers that size an icon numerically. */
export const ICON_SIZE = {
  /** Inline and action icons. */
  inline: 16,
  /** Overflow menus and status icons. */
  status: 20,
  /** Empty states and dialog semantics. */
  display: 24,
} as const;

/** Rule I5 as Tailwind utilities, which is how components usually size icons. */
export const ICON_SIZE_CLASS = {
  inline: 'size-4',
  status: 'size-5',
  display: 'size-6',
} as const;

/**
 * Rule R4. The only place in the application that mirrors an icon for RTL.
 * It is a CSS variant rather than a direction lookup, so it also holds inside
 * portalled content that never re-renders on a locale change.
 */
export const MIRRORED_ICON_CLASS = 'rtl:-scale-x-100';

function decorate(Icon: IconComponent, extraClassName: string, displayName: string): IconComponent {
  function DecoratedIcon({ className, ...props }: LucideProps) {
    return createElement(Icon, { ...props, className: cn(extraClassName, className) });
  }
  DecoratedIcon.displayName = displayName;
  return DecoratedIcon;
}

/**
 * Wraps a directional icon so it mirrors with the document direction. Use this
 * instead of attaching an `rtl:` variant at the call site (rule R4). Icons that
 * must never mirror — brand marks, numerals, media controls (rule R5) — are
 * simply not wrapped.
 */
export function mirrorIcon(Icon: IconComponent, displayName: string): IconComponent {
  return decorate(Icon, MIRRORED_ICON_CLASS, displayName);
}

/** Create / add a business entity. */
export const IconCreate: IconComponent = Plus;
/** Edit an existing record. */
export const IconEdit: IconComponent = Pencil;
/** Delete a record. */
export const IconDelete: IconComponent = Trash2;
/** Save changes. */
export const IconSave: IconComponent = Save;
/** Confirm / submit a decision. */
export const IconConfirm: IconComponent = Check;
/** Cancel an action, and close a surface. */
export const IconCancel: IconComponent = X;
/** Search. */
export const IconSearch: IconComponent = Search;
export const IconCopy: IconComponent = Copy;
export const IconServices: IconComponent = ListChecks;
/** Open or apply filters. */
export const IconFilter: IconComponent = Funnel;
/** Clear every active filter. */
export const IconResetFilters: IconComponent = RotateCcw;
/** Row and toolbar overflow menu. */
export const IconOverflow: IconComponent = EllipsisVertical;
/** View the details of a record. */
export const IconViewDetails: IconComponent = Eye;
/** Print. */
export const IconPrint: IconComponent = Printer;
/** Export to a spreadsheet. */
export const IconExportSpreadsheet: IconComponent = FileSpreadsheet;
/** Download a file. */
export const IconDownload: IconComponent = Download;
/** Refresh, or change state. */
export const IconRefresh: IconComponent = RefreshCw;
/** Send / post. */
export const IconSend: IconComponent = Send;
/** Navigate back. Mirrors with the direction. */
export const IconBack: IconComponent = mirrorIcon(ArrowLeft, 'IconBack');
/** Navigate to a related record. Mirrors with the direction. */
export const IconNavigateToRecord: IconComponent = mirrorIcon(ArrowUpRight, 'IconNavigateToRecord');
/** Busy. Carries its own animation so no caller has to remember it. */
export const IconBusy: IconComponent = decorate(LoaderCircle, 'animate-spin', 'IconBusy');
/** Success status. */
export const IconSuccess: IconComponent = CircleCheck;
/** Error status. */
export const IconError: IconComponent = CircleX;
/** Warning status. */
export const IconWarning: IconComponent = TriangleAlert;
/** Information status. */
export const IconInfo: IconComponent = Info;

/**
 * Additional icons for navigation, domain concepts and lifecycle actions that
 * appear in product code but are not one of the 23 actions catalogued above
 * (rule I3 only names those). They still route through this module, so rule
 * I1 continues to hold — lucide-react is imported nowhere else in product
 * code. None of these belong in `ICONS`/`ICON_NAMES`, which mirror the
 * UI/UX principles table exactly and nothing more.
 */
/** Language switch. */
export const IconLanguage: IconComponent = Languages;
/** Switch to the dark theme. */
export const IconThemeDark: IconComponent = Moon;
/** Switch to the light theme. */
export const IconThemeLight: IconComponent = Sun;
/** Identity: review center, identity-conflict cases. */
export const IconIdentity: IconComponent = Fingerprint;
/** Information-completeness and reliability classification. */
export const IconClassification: IconComponent = Layers3;
/** A connected integration source. */
export const IconIntegration: IconComponent = Network;
/** Permission groups and access control. */
export const IconAccess: IconComponent = ShieldCheck;
/** The systems launcher (nav-user's "الأنظمة", hands off to Unified Access). */
export const IconApplications: IconComponent = Grid2x2;
/** "تغيير الفرع" (nav-user, hands off to Unified Access). */
export const IconBranch: IconComponent = Building2;
/** The customer directory. */
export const IconCustomers: IconComponent = UsersRound;
/** Expands a trigger's attached menu. */
export const IconMenuExpand: IconComponent = ChevronsUpDown;
/** Sign out. */
export const IconLogout: IconComponent = LogOut;
/** Generic user / account avatar placeholder. */
export const IconUser: IconComponent = User;
/** A branch or physical location. */
export const IconLocation: IconComponent = MapPin;
/** Merge two records. */
export const IconMerge: IconComponent = GitMerge;
/** A sortable column header's collapsed (unsorted) state. */
export const IconSortable: IconComponent = ChevronsUpDown;
/** A sortable column header's active sort direction toggle. */
export const IconExpandToggle: IconComponent = ChevronDown;
/** currency.md section 3.2: عكس العملية - a reversed movement. */
export const IconReverse: IconComponent = Undo2;
/** currency.md section 3.3: تغيير الحالة - activate/deactivate a record. */
export const IconStatus: IconComponent = Power;
/** An audit or reliability history entry. */
export const IconHistory: IconComponent = History;
/** An integration source's API credential. */
export const IconApiKey: IconComponent = KeyRound;
/** A linked or associated record. */
export const IconLink: IconComponent = Link2;
/** No results found. */
export const IconNoResults: IconComponent = SearchX;
/**
 * Restore a previously removed record. Shares its glyph with the
 * catalogued reset-filters action (both read as "revert"), but names a
 * distinct lifecycle action; reset-filters has no current call site.
 */
export const IconRestore: IconComponent = RotateCcw;
/** Previous page. Mirrors with the direction. */
export const IconPreviousPage: IconComponent = mirrorIcon(ChevronLeft, 'IconPreviousPage');
/** Next page. Mirrors with the direction. */
export const IconNextPage: IconComponent = mirrorIcon(ChevronRight, 'IconNextPage');

/**
 * Navigation-section icons for the app sidebar (`components/layout/nav-main.tsx`)
 * and the dashboard's matching "new exchange" action. Not part of the I3 table,
 * which catalogues generic workflow actions rather than this application's own
 * fixed set of top-level sections — each is still one glyph per destination.
 *
 * currency.md's own nav order (section 3): عملية صرف، الصناديق، العملات،
 * الخزينة، تقارير وإحصائيات، الإعدادات → المستخدمين، المجموعات.
 */
export const IconNavHome: IconComponent = Home;
/** Also used outside the sidebar wherever "start a new exchange" appears. */
export const IconNavExchange: IconComponent = ArrowLeftRight;
export const IconNavTransactions: IconComponent = List;
/** الصناديق (boxes). */
export const IconNavCash: IconComponent = Wallet;
/** العملات (currencies). */
export const IconNavRates: IconComponent = Landmark;
/** الخزينة (treasury). */
export const IconNavTreasury: IconComponent = Vault;
export const IconNavReports: IconComponent = ChartNoAxesCombined;
export const IconNavConfiguration: IconComponent = Settings;
export const IconNavAccess: IconComponent = Users;
/** المستخدمين. */
export const IconNavUsers: IconComponent = User;
/** المجموعات. */
export const IconNavGroups: IconComponent = UsersRound;
export const IconNavAudit: IconComponent = Shield;
export const IconNavIntegration: IconComponent = Activity;

/**
 * Change-password menu item (`components/layout/nav-user.tsx`). Shares its
 * glyph with `IconApiKey` deliberately — both are key-shaped credential
 * concepts — while remaining a distinct named export for its own action, the
 * same precedent `IconRestore`/`IconResetFilters` already set below.
 */
export const IconChangePassword: IconComponent = KeyRound;

/**
 * The disclosure chevron shared by every accordion/collapsible/select-style
 * "expand this panel" control (`CollapseComponent`, `FilterableSelectComponent`,
 * `SearchItemsDropdown`, and the `<details>`-style sections in
 * `features/currency/components.tsx` and `exchange.tsx`). Down does not need
 * mirroring (rule R5 is about not mirroring what direction cannot affect).
 */
export const IconDisclosure: IconComponent = ChevronDown;
/**
 * A tree node's collapsed state (`components/shared/TreeView.tsx`): points
 * toward the reading-end, mirroring with the direction (rule R4) instead of
 * the hand-picked `ChevronLeft`/`ChevronRight` swap it replaces.
 */
export const IconDisclosureCollapsed: IconComponent = mirrorIcon(
  ChevronRight,
  'IconDisclosureCollapsed',
);

/**
 * A sidebar group's disclosure chevron (section 41, rule NV8). The call site
 * wraps the trigger's `Collapsible` root with `className="group/collapsible"`
 * so this icon can react to that group's own `data-state` via Tailwind's
 * `group-data-.../collapsible:` variant — no `isOpen` prop needed here.
 *
 * While the group is closed it points toward the inline-end edge, mirrored
 * through the shared `rtl:` convention (rule R4) rather than a hand-attached
 * direction check. Once open it rotates to point straight down, in both
 * directions — the mirror is scoped to the closed state only, because
 * combining it with the open rotation would point the glyph up instead of
 * down in Arabic. That combination is exactly the defect section 17 records
 * in the reference sidebar: `ChevronRight` rotated a fixed 90° with no
 * regard for the mirror, so in Arabic the affordance points away from what
 * it opens. Positioning (`ms-auto`) is the call site's job, same as any
 * other icon; this component only ever carries logical classes.
 */
export const IconNavDisclosure: IconComponent = decorate(
  ChevronRight,
  'transition-transform group-data-[state=closed]/collapsible:rtl:-scale-x-100 group-data-[state=open]/collapsible:rotate-90',
  'IconNavDisclosure',
);

/**
 * Image capture and editing actions used by the file-upload family
 * (`FileUploadComponent`, `MultiFileUploadComponent`, `ImageViewerModal`).
 */
export const IconUpload: IconComponent = Upload;
export const IconCameraCapture: IconComponent = Camera;
export const IconCrop: IconComponent = Crop;
/** Rotate an image. Distinct from `IconRestore`'s counter-clockwise revert. */
export const IconRotate: IconComponent = RotateCw;
export const IconZoomIn: IconComponent = ZoomIn;
export const IconZoomOut: IconComponent = ZoomOut;

/** A date field (`components/shared/DateTimePickerComponent.tsx`). */
export const IconCalendar: IconComponent = Calendar;
/** An NFC login card prompt (`components/shared/NFCDialog.tsx`). */
export const IconNfcCard: IconComponent = CreditCard;
/** A catalog product (`ProductSelectComponent`, `SearchItemsDropdown`). */
export const IconProduct: IconComponent = Package;
/** A product's unit of measure (`ProductUnitSelectComponent`). */
export const IconProductUnit: IconComponent = Hash;
/** A product unit/variant row in a search result (`SearchItemsDropdown`). */
export const IconLayers: IconComponent = Layers;
/** A tree-view folder/branch node (`TreeView`, `TreeViewDemo`). */
export const IconFolder: IconComponent = Folder;
/** An expanded tree-view folder node (`TreeViewDemo`). */
export const IconFolderOpen: IconComponent = FolderOpen;
/** A tree-view leaf/file node (`TreeView`). */
export const IconFile: IconComponent = File;
/** A report file in a tree view (`TreeViewDemo`). */
export const IconFileReport: IconComponent = FileBarChart;
/** A text/template document in a tree view (`TreeViewDemo`). */
export const IconFileDocument: IconComponent = FileText;
/** A code/config file in a tree view (`TreeViewDemo`). */
export const IconFileCode: IconComponent = FileCode;
/** A settings/configuration folder in a tree view (`TreeViewDemo`). */
export const IconFolderSettings: IconComponent = FolderCog;

/** The icon table of the UI/UX principles, in the order it is written there. */
export const ICONS = {
  create: IconCreate,
  edit: IconEdit,
  delete: IconDelete,
  save: IconSave,
  confirm: IconConfirm,
  cancel: IconCancel,
  search: IconSearch,
  filter: IconFilter,
  resetFilters: IconResetFilters,
  overflow: IconOverflow,
  viewDetails: IconViewDetails,
  print: IconPrint,
  exportSpreadsheet: IconExportSpreadsheet,
  download: IconDownload,
  refresh: IconRefresh,
  send: IconSend,
  back: IconBack,
  navigateToRecord: IconNavigateToRecord,
  busy: IconBusy,
  success: IconSuccess,
  error: IconError,
  warning: IconWarning,
  info: IconInfo,
} as const satisfies Record<string, IconComponent>;

export type IconName = keyof typeof ICONS;

/** Every semantic action the icon table covers, in table order. */
export const ICON_NAMES = Object.keys(ICONS) as readonly IconName[];
